import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  User,
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyBiu2hc4IJVeAEiuWIi5SHuTKyoIN3LNKw',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'cerebro-sign.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'cerebro-sign',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'cerebro-sign.firebasestorage.app',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '59708889751',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:59708889751:web:c6032723f3e2898f8867d2',
};

import { getFirestore } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

export const googleProvider = new GoogleAuthProvider();
// Explicitly request Gmail Settings Basic permission for direct signature injection
googleProvider.addScope('https://www.googleapis.com/auth/gmail.settings.basic');

// Prompt to select Google account so user can pick their real physical account
googleProvider.setCustomParameters({
  prompt: 'select_account',
});

export interface GoogleLoginResult {
  user: User;
  accessToken: string | null;
}

export async function loginWithGoogle(): Promise<GoogleLoginResult> {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    // Extract OAuth 2.0 Access Token with gmail.settings.basic scope
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const accessToken = credential?.accessToken || null;

    if (accessToken) {
      sessionStorage.setItem('cerebro_google_access_token', accessToken);
      localStorage.setItem('cerebro_google_access_token', accessToken);
    }

    return {
      user: result.user,
      accessToken,
    };
  } catch (error: any) {
    console.error('[Firebase Auth] Sign in error:', error);
    throw error;
  }
}

export function getStoredGoogleAccessToken(): string | null {
  return sessionStorage.getItem('cerebro_google_access_token') || localStorage.getItem('cerebro_google_access_token');
}

export async function ensureGoogleAccessToken(): Promise<string> {
  let token = getStoredGoogleAccessToken();
  if (token) {
    return token;
  }
  const result = await loginWithGoogle();
  if (!result.accessToken) {
    throw new Error('BRAK_TOKENU_GOOGLE: Nie udało się uzyskać tokenu Google z uprawnieniem do Gmaila.');
  }
  return result.accessToken;
}

export async function logoutUser(): Promise<void> {
  sessionStorage.removeItem('cerebro_google_access_token');
  localStorage.removeItem('cerebro_google_access_token');
  await signOut(auth);
}

/**
 * Walidacja pliku po stronie klienta (przed wywołaniem uploadBytes):
 * - Sprawdź, czy file.size <= 5 * 1024 * 1024 (max 5 MB).
 * - Sprawdź, czy file.type.startsWith('image/').
 */
export function validateStorageFile(file: File): void {
  if (!file) {
    throw new Error('Nie wybrano pliku do przesłania.');
  }
  if (!file.type || !file.type.startsWith('image/')) {
    throw new Error('Wybrany plik musi być plikiem graficznym (image/*).');
  }
  if (file.size > 5 * 1024 * 1024) {
    throw new Error('Rozmiar pliku nie może przekraczać 5 MB.');
  }
}

/**
 * Generuje ścieżkę do zapisu w Firebase Storage zgodnie z regułami:
 * Sprawdź domenę email zalogowanego użytkownika (user.email):
 * 1. Jeśli to konto Gmail (gmail.com lub googlemail.com):
 *    tenants/gmail.com/users/${user.uid}/assets/${uniqueFileName}
 * 2. Jeśli to domena Workspace (dowolna inna niż gmail.com):
 *    tenants/${domain}/assets/${uniqueFileName}
 */
export function generateStoragePath(domain: string, fileName: string): string {
  const user = auth.currentUser;
  const userEmail = (user?.email || '').toLowerCase().trim();
  const emailDomain = userEmail.includes('@') ? userEmail.split('@')[1] : '';
  const isGmail = emailDomain === 'gmail.com' || emailDomain === 'googlemail.com';

  const uniqueToken = Math.random().toString(36).substring(2, 9) + '_' + Date.now();
  const safeFileName = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
  const uniqueFileName = `${uniqueToken}_${safeFileName}`;

  if (isGmail && user?.uid) {
    return `tenants/gmail.com/users/${user.uid}/assets/${uniqueFileName}`;
  }

  const cleanDomain = (domain && domain.trim()) || emailDomain || 'cerebrolabs.io';
  return `tenants/${cleanDomain}/assets/${uniqueFileName}`;
}

/**
 * Upload an image asset (logo, banner, template graphic) to Firebase Storage.
 * Returns the public download URL to be inserted directly into <img src="..."> in the signature HTML.
 */
export async function uploadAsset(
  file: File,
  domain: string
): Promise<string> {
  // 1. Walidacja po stronie klienta (przed wywołaniem uploadBytes)
  validateStorageFile(file);

  try {
    const storagePath = generateStoragePath(domain, file.name);
    const storageRef = ref(storage, storagePath);

    const user = auth.currentUser;
    await uploadBytes(storageRef, file, {
      contentType: file.type || 'image/png',
      customMetadata: {
        domain,
        userEmail: user?.email || '',
        userId: user?.uid || '',
        uploadedAt: new Date().toISOString(),
      },
    });

    const downloadUrl = await getDownloadURL(storageRef);
    return downloadUrl;
  } catch (error: any) {
    console.error('[Firebase Storage Upload Error]:', error);
    throw error;
  }
}

/**
 * Upload an employee personal avatar to Firebase Storage.
 * Uses the tenant-isolated path and client validation.
 * Returns the public download URL to be stored in the employee profile.
 */
export async function uploadEmployeeAvatar(
  file: File,
  domain: string,
  _userId?: string
): Promise<string> {
  // 1. Walidacja po stronie klienta (przed wywołaniem uploadBytes)
  validateStorageFile(file);

  try {
    const storagePath = generateStoragePath(domain, file.name);
    const storageRef = ref(storage, storagePath);

    const user = auth.currentUser;
    await uploadBytes(storageRef, file, {
      contentType: file.type || 'image/png',
      customMetadata: {
        domain,
        userEmail: user?.email || '',
        userId: user?.uid || '',
        uploadedAt: new Date().toISOString(),
      },
    });

    const downloadUrl = await getDownloadURL(storageRef);
    return downloadUrl;
  } catch (error: any) {
    console.error('[Firebase Storage Upload Avatar Error]:', error);
    throw error;
  }
}

export { onAuthStateChanged };


